import React, { FunctionComponent } from "react";
import { useReactFlow, useUpdateNodeInternals, Node, Edge } from "reactflow";
import { IDataColumnDefProps, ITextQuery, PickerInput, IBannerContext, BannerStatus } from "@plex/react-components";
import { DataType, IDataSourceAPI } from "../NodeTypes/TypeDefinitions";
import {
  getDsApiInputs,
  getDsApiOutputs,
  setResultDataSourceApiSpec
} from "../NodeTypes/DataSourceApi/DataSourceAPiSpecs";
import { INodeConfigPropertiesProps } from "../NodeTypes/PropertyTypeDefinitions";
import {
  addObjectListSchema,
  addObjectSchema,
  getDocumentObjectSchemaFromProperties,
  getEmptySchemaId,
  getObjectSchemaIdWithSource,
  getSchema,
  getSchemaId,
  IDesignerSchema
} from "../NodeTypes/DataSchemas";
import { DocumentSchemaSource, IDataParam } from "../NodeTypes/Base/NodeTypeDefinitions";
import { useFunctionSubscriber } from "../FunctionSubscriberContext/FunctionSubscriberContext";
import {
  allEdgesDeletionMode,
  checkAndRemoveUnmappable,
  deleteEdgesFromNodes,
  getNameFromDataHandleId,
  INewEdgeSchema,
  updateLinkedSchemas
} from "../Util/EdgeUtil";
import "./PickerFormField.scss";
import { useViewController } from "../ViewContext";
import { convertKeysToCamelCaseArray, convertKeysToLowerCaseNested } from "../Util/KeyFormatter";
import { DocumentSchemaType } from "../FlowDocument/FlowDocumentModel";

export const DataSourcePickerFormField: FunctionComponent<INodeConfigPropertiesProps> = (props) => {
  const reactFlowInstance = useReactFlow();
  const updateNodeInternals = useUpdateNodeInternals();
  const viewController = useViewController();
  const [{ dsApiPickerSelected, dsApiPickerData, searchText }, setDsApiPicker] = React.useState({
    dsApiPickerSelected: props.node?.data.nodeProperties.dataSourceId
      ? [
          {
            id: props.node?.data.nodeProperties.dataSourceId,
            name: props.node?.data.nodeProperties.dataSourceName
          } as IDataSourceAPI
        ]
      : ([] as IDataSourceAPI[]),
    dsApiPickerData: [] as { id: string; name: string }[],
    searchText: ""
  });
  const [, _setState] = React.useState({ value: props.node?.data.nodeProperties || {} });
  const [recordLimitExceededState, setRecordLimitExceededState] = React.useState(false);
  const [searchErrorState, setSearchErrorState] = React.useState("");
  const { plexFetchDsApi, plexFetchDsDetailApi, plexShowOverlay } = useFunctionSubscriber();

  const getNodeInputs = (nodeType: string, category: string, apiTitle: string, apiId: string) => {
    if (nodeType === "callDataSource") {
      return getDsApiInputs(category, apiTitle, apiId);
    }

    return [];
  };

  const getNodeOutputs = (node: Node<any>) => {
    if (node!.data.nodeProperties.dataSourceId) {
      return getDsApiOutputs("Commands", node!.data.nodeProperties.dataSourceId.toString());
    }
    return [];
  };

  const handleDsPickerSearch = (query: ITextQuery, cols: Array<IDataColumnDefProps<IDataSourceAPI>>) => {
    let dsApis: { id: string; name: string }[] = [];

    if (plexFetchDsApi) {
      plexFetchDsApi(query?.displayText)
        .then((res: any) => {
          const resultData = convertKeysToCamelCaseArray(res.Data);

          setRecordLimitExceededState(false);
          setSearchErrorState("");

          if (cols.length === 0) {
            dsApis = resultData.filter((x) => (x.name || "").toLowerCase().includes(query.query.toLowerCase()));
          } else {
            dsApis = resultData.filter((row: any) =>
              query.isMatch(cols.map((col) => (col.valueSelector ? String(col.valueSelector(row)) : "")))
            );
          }

          setDsApiPicker((x: any) => ({ ...x, dsApiPickerData: dsApis }));
        })
        .catch((e) => {
          setSearchErrorState("An error occurred when searching.");
          setRecordLimitExceededState(false);
          console.error(e);
        });
    }
  };

  const handleDsPickerSearchTextChanged = (value: string) => {
    setDsApiPicker((x: any) => ({ ...x, searchText: value }));
  };

  const loadNodeDetail = (values: any, props: INodeConfigPropertiesProps, reactFlowInstance, currentEdges?: Edge[]) => {
    if (values.length > 0) {
      props.node!.data.nodeProperties.dataSourceId = parseInt(values[0].id);
      props.node!.data.nodeProperties.dataSourceName = values[0].name;
    } else {
      props.node!.data.nodeProperties.dataSourceId = null;
      props.node!.data.nodeProperties.dataSourceName = null;
    }
    props.node!.data.nodeProperties.inputs = {};
    props.node!.data.nodeProperties.outputs.outputs.schemaId = getEmptySchemaId(DataType.OBJECT);
    props.node!.data.nodeProperties.outputs.rows.schemaId = getEmptySchemaId(DataType.OBJECTLIST);
    const newEdges = deleteEdgesFromNodes(
      [props.node!.id],
      reactFlowInstance,
      updateNodeInternals,
      {
        ...allEdgesDeletionMode,
        controlEdges: false,
        outputEdges: false
      },
      currentEdges
    );

    if (values.length === 0) {
      props.forceNodeUpdate();
      return newEdges;
    }

    getNodeInputs(
      props.node?.data.nodeProperties.type,
      "Commands",
      "",
      props.node.data.nodeProperties.dataSourceId.toString()
    ).forEach((input: any) => {
      if (!props.node!.data.nodeProperties.inputs[input.name]) {
        props.node!.data.nodeProperties.inputs[input.name] = {
          schemaId: getSchemaId(input.type),
          required: input.required === true,
          type: input.type,
          enabled: input.required === true,
          wireUp: ""
        };
      }
    });

    getNodeOutputs(props.node!).forEach((output: any) => {
      const schemaId: string = getSchemaId(output.type, props.node, [output.name]);
      props.node!.data.nodeProperties.outputs[output.name] = {
        schemaId: schemaId ?? getEmptySchemaId(output.type),
        enabled: true,
        channel: output.channel,
        type: output.type,
        isArray: output.isArray,
        isErrorCode: output.isErrorCode
      };
    });

    props.forceNodeUpdate();

    return newEdges;
  };

  const loadDsApiDetail = (
    values: any,
    props: INodeConfigPropertiesProps,
    reactFlowInstance,
    bannerContext?: IBannerContext,
    callback?: () => void
  ) => {
    let result: any;

    if (values.length === 0) {
      if (callback) {
        callback();
      }
      return;
    }

    const id = values[0].id;
    if (plexFetchDsDetailApi) {
      plexFetchDsDetailApi(id)
        .then((res) => {
          const response = JSON.parse(res.Data.DataSource);
          result = convertKeysToLowerCaseNested(response);

          setResultDataSourceApiSpec(result);

          result.id = id;
          result.inputs = result.inputs.filter((i: any) => i.name !== "Plexus_Customer_No" && i.name !== "PCN");

          sessionStorage.setItem("dsApi_" + id, JSON.stringify(result));

          plexShowOverlay && plexShowOverlay(false);

          const outputsOutputProperties = getDsApiOutputs("Commands", values[0].id, false, true);
          const rowsOutputProperties = getDsApiOutputs("Commands", values[0].id, true, false);

          const schemaSource: {
            sourceSystem: DocumentSchemaSource;
            sourceId: any;
          } = { sourceSystem: DocumentSchemaSource.dataSource, sourceId: id };
          const outputsObjectSchemaId = getObjectSchemaIdWithSource(DataType.OBJECT, ["outputs"], schemaSource);
          const rowsObjectSchemaId = getObjectSchemaIdWithSource(DataType.OBJECT, ["rows"], schemaSource);
          const rowsObjectListSchemaId = getObjectSchemaIdWithSource(DataType.OBJECTLIST, ["rows"], schemaSource);

          const getSchemaMetadata = (dataSourceName: string) => {
            return { dataSourceId: parseInt(values[0].id), dataSourceName: dataSourceName.replaceAll("_", " ") };
          };
          const outputsName = "Outputs";
          const rowsName = "Row";

          const outputsObjectSchema = getDocumentObjectSchemaFromProperties(
            outputsObjectSchemaId,
            outputsOutputProperties as IDataParam[]
          );
          outputsObjectSchema.metadata = getSchemaMetadata(values[0].name + ` ${outputsName}`);
          const rowsObjectSchema = getDocumentObjectSchemaFromProperties(
            rowsObjectSchemaId,
            rowsOutputProperties as IDataParam[]
          );
          rowsObjectSchema.metadata = getSchemaMetadata(values[0].name + ` ${rowsName}`);
          const rowsObjectSchemaList = {
            id: rowsObjectListSchemaId,
            schemaType: DocumentSchemaType.list,
            listItemSchema: rowsObjectSchemaId,
            metadata: getSchemaMetadata(values[0].name + ` ${rowsName}`)
          };
          addObjectSchema(outputsObjectSchema, DocumentSchemaSource.dataSource, id);
          addObjectSchema(rowsObjectSchema, DocumentSchemaSource.dataSource, id);
          addObjectListSchema(rowsObjectSchemaList, DocumentSchemaSource.dataSource, id);

          if (callback) {
            callback();
          }
        })
        .catch((e) => {
          const sessionStorageDsApi = sessionStorage.getItem("dsApi_" + id);
          if (sessionStorageDsApi) {
            result = JSON.parse(sessionStorageDsApi);
          } else {
            bannerContext?.addMessage("An error occurred retrieving data source metadata.", BannerStatus.error);
            console.log(e);
          }
          plexShowOverlay && plexShowOverlay(false);
        });
    }
  };

  return (
    <PickerInput<IDataSourceAPI>
      key="Data Source Picker"
      dialogTitle="Data Source"
      initialWidth={900}
      disabled={false}
      selected={dsApiPickerSelected}
      onSelectionChanged={(values: any) => {
        if (values.length === 0) {
          loadDsApiDetail(values, props, reactFlowInstance, viewController.bannerContext, () => {
            const outputEdges = reactFlowInstance.getEdges().filter((e) => e.target === props.node!.id);
            const outputEdgeSchemas: INewEdgeSchema[] = outputEdges.map((edge: Edge) => {
              return { edge: edge, newSchema: undefined };
            });
            checkAndRemoveUnmappable(
              outputEdgeSchemas,
              (edges: Edge[]) => {
                setDsApiPicker((x) => ({ ...x, dsApiPickerSelected: [], searchText: "" }));
                const newEdges = loadNodeDetail(values, props, reactFlowInstance, edges);
                updateLinkedSchemas(reactFlowInstance.getNodes(), newEdges, updateNodeInternals, reactFlowInstance);
              },
              reactFlowInstance,
              updateNodeInternals
            );
          });
          return;
        }

        if (props.node!.data.nodeProperties.dataSourceId === values[0]?.id) {
          return;
        }
        setDsApiPicker((x) => ({ ...x, dsApiPickerSelected: values.length > 0 ? [values[0]] : [], searchText: "" }));

        if (values.length > 0) {
          plexShowOverlay && plexShowOverlay(true);
          // load
          loadDsApiDetail(values, props, reactFlowInstance, undefined, () => {
            const schemaSource: {
              sourceSystem: DocumentSchemaSource;
              sourceId: any;
            } = { sourceSystem: DocumentSchemaSource.dataSource, sourceId: values[0]?.id };
            const outputsSchemaId = getObjectSchemaIdWithSource(DataType.OBJECT, ["outputs"], schemaSource);
            const rowsSchemaId = getObjectSchemaIdWithSource(DataType.OBJECTLIST, ["rows"], schemaSource);
            const outputEdges = reactFlowInstance.getEdges().filter((e) => e.target === props.node!.id);
            const outputEdgeSchemas: INewEdgeSchema[] = outputEdges
              .map((edge: Edge) => {
                const outputName = getNameFromDataHandleId(edge.targetHandle!);
                let schema: IDesignerSchema | undefined = undefined;
                switch (outputName) {
                  case "outputs":
                    schema = getSchema(outputsSchemaId);
                    break;
                  case "rows":
                    schema = getSchema(rowsSchemaId);
                    break;
                  default:
                    return undefined;
                }
                return { edge: edge, newSchema: schema };
              })
              .filter((edge: INewEdgeSchema | undefined) => edge !== undefined) as INewEdgeSchema[];
            checkAndRemoveUnmappable(
              outputEdgeSchemas,
              (edges: Edge[]) => {
                const newEdges = loadNodeDetail(values, props, reactFlowInstance, edges);
                updateLinkedSchemas(reactFlowInstance.getNodes(), newEdges, updateNodeInternals, reactFlowInstance);
                setTimeout(() => props.forceNodeUpdate(), 80);
              },
              reactFlowInstance,
              updateNodeInternals
            );
          });
        }
      }}
      keySelector={(row: IDataSourceAPI) => row?.id}
      displaySelector={(row: IDataSourceAPI) => row?.name}
      data={dsApiPickerData}
      onSearch={handleDsPickerSearch}
      searchDisabled={false}
      isOpen={false}
      multiSelect={false}
      maxDisplay={500}
      recordLimitExceeded={recordLimitExceededState}
      errorMessage={searchErrorState ? searchErrorState : undefined}
      searchText={searchText}
      onSearchTextChange={handleDsPickerSearchTextChanged}
    >
      <PickerInput.Column<IDataSourceAPI>
        key="1"
        id="DataSourceDatabaseName"
        title="Database"
        valueSelector={(row: IDataSourceAPI) => row.database}
      >
        {(row: IDataSourceAPI) => row.database}
      </PickerInput.Column>

      <PickerInput.Column<IDataSourceAPI>
        key="2"
        id="DataSourceName"
        title="Name"
        valueSelector={(row: IDataSourceAPI) => row.storedProcedure}
        searchable
      >
        {(row: IDataSourceAPI) => row.storedProcedure}
      </PickerInput.Column>

      <PickerInput.Column<IDataSourceAPI>
        key="3"
        id="DataSourceInputCount"
        title="Inputs"
        valueSelector={(row: IDataSourceAPI) => row.inputCount}
      >
        {(row: IDataSourceAPI) => row.inputCount}
      </PickerInput.Column>

      <PickerInput.Column<IDataSourceAPI>
        key="4"
        id="DataSourceOutputCount"
        title="Outputs"
        valueSelector={(row: IDataSourceAPI) => row.outputCount}
      >
        {(row: IDataSourceAPI) => row.outputCount}
      </PickerInput.Column>
    </PickerInput>
  );
};
